/**
 * Where the surface sits, for both of its layers.
 *
 * Ported from the spike's `call-surface.tsx`, minus the branching it existed to
 * hold together. Two rules survive it intact:
 *
 * 1. Expanded is MEASURED from `anchorId`, not hardcoded. `absolute` scrolls
 *    away with the page; `fixed inset-0` swallows the app's sidebar and header.
 *    Neither is what somebody wants mid-consult.
 * 2. The minimised card snaps to a corner rather than being left where it was
 *    dropped: a card parked mid-screen covers the chart, and a corner is one
 *    less thing to tidy up afterwards.
 */
import { type CSSProperties, type PointerEvent, useCallback, useEffect, useState } from "react";

/** Which corner the minimised card rests in. */
type Corner = "tl" | "tr" | "bl" | "br";

const CORNER_MARGIN = 20;
const CORNER_STORAGE_KEY = "call-pip-corner";

const cornerStyle = (corner: Corner): CSSProperties => ({
  ...(corner[0] === "t" ? { top: CORNER_MARGIN } : { bottom: CORNER_MARGIN }),
  ...(corner[1] === "l" ? { left: CORNER_MARGIN } : { right: CORNER_MARGIN }),
});

function readCorner(): Corner {
  try {
    const stored = localStorage.getItem(CORNER_STORAGE_KEY);
    if (stored === "tl" || stored === "tr" || stored === "bl" || stored === "br") return stored;
  } catch {
    // Private mode or blocked storage — the default corner is fine.
  }
  return "br";
}

/** Tracks an element's viewport rect across resizes and layout changes. */
function useAnchorRect(anchorId: string | undefined): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!anchorId) return;
    const anchor = document.getElementById(anchorId);
    if (!anchor) return;
    const measure = () => setRect(anchor.getBoundingClientRect());
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(anchor);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [anchorId]);

  return rect;
}

/** The waiting room column's width when open. Matches `w-80` on the aside. */
export const ASIDE_WIDTH = 320;

export interface SurfaceGeometry {
  minimised: boolean;
  /** The whole surface region: the stage AND the column beside it. */
  style: CSSProperties;
  /**
   * The stage alone, inset by the column when it is open.
   *
   * A SEPARATE STYLE RATHER THAN PADDING, because the stage is its own `fixed`
   * portal — the chrome cannot contain it, so the only way to make room beside
   * it is to move its edge. That is what makes the waiting room a column the
   * video sits next to rather than a panel floating on top of it.
   */
  stageStyle: CSSProperties;
  /**
   * The waiting room column's own layer, at the region's right edge.
   *
   * Its own layer rather than a child of the chrome, because the chrome is
   * positioned against the STAGE — that is what keeps minimise on the call and
   * the controls centred under the video rather than under video-plus-column.
   * Width collapses to zero when shut so the handle, pinned to its left edge,
   * lands on the region's edge instead of floating 320px inside it.
   */
  asideStyle: CSSProperties;
  asideOpen: boolean;
  dragging: boolean;
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
}

/**
 * One geometry for both layers. The drag offset lives here rather than on the
 * chrome, so the stage moves with the card instead of being left behind by it.
 */
export function useSurfaceGeometry(
  minimised: boolean,
  anchorId: string | undefined,
  /** True only when the surface HAS a waiting room and it is slid open. */
  asideOpen = false,
): SurfaceGeometry {
  const rect = useAnchorRect(anchorId);
  const [corner, setCorner] = useState<Corner>(readCorner);
  const [offset, setOffset] = useState<{ x: number; y: number } | null>(null);

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      // Let the controls be controls — only the video and empty chrome drag.
      if (!minimised || (event.target as HTMLElement).closest("button, input, a, form")) return;
      const card = event.currentTarget;

      const start = { x: event.clientX, y: event.clientY };
      const from = card.getBoundingClientRect();
      try {
        card.setPointerCapture(event.pointerId);
      } catch {
        // Throws if the pointer is already gone; the drag still tracks fine.
      }

      const move = (moveEvent: globalThis.PointerEvent) =>
        setOffset({ x: moveEvent.clientX - start.x, y: moveEvent.clientY - start.y });

      const end = (endEvent: globalThis.PointerEvent) => {
        const centreX = from.left + (endEvent.clientX - start.x) + from.width / 2;
        const centreY = from.top + (endEvent.clientY - start.y) + from.height / 2;
        const next = `${centreY < window.innerHeight / 2 ? "t" : "b"}${
          centreX < window.innerWidth / 2 ? "l" : "r"
        }` as Corner;
        setCorner(next);
        try {
          localStorage.setItem(CORNER_STORAGE_KEY, next);
        } catch {
          // Storage is a convenience here, never a requirement.
        }
        setOffset(null);
        card.removeEventListener("pointermove", move);
        card.removeEventListener("pointerup", end);
        card.removeEventListener("pointercancel", end);
      };

      card.addEventListener("pointermove", move);
      card.addEventListener("pointerup", end);
      card.addEventListener("pointercancel", end);
    },
    [minimised],
  );

  const style: CSSProperties = minimised
    ? { ...cornerStyle(corner), ...(offset ? { transform: `translate(${offset.x}px, ${offset.y}px)` } : {}) }
    : rect
      ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
      : { inset: 0 };

  // The minimised card has no room for a column and never shows one, so the
  // two styles are the same there and the aside is simply not rendered.
  const inset = !minimised && asideOpen;
  const stageStyle: CSSProperties = !inset
    ? style
    : rect
      ? { top: rect.top, left: rect.left, width: Math.max(rect.width - ASIDE_WIDTH, 0), height: rect.height }
      : { top: 0, bottom: 0, left: 0, right: ASIDE_WIDTH };

  // Present whenever the surface has a column at all, so the handle survives
  // the column being shut. Zero-width when shut; `inset` is what "open" means.
  const asideWidth = inset ? ASIDE_WIDTH : 0;
  const asideStyle: CSSProperties = rect
    ? { top: rect.top, height: rect.height, left: rect.left + rect.width - asideWidth, width: asideWidth }
    : { top: 0, bottom: 0, right: 0, width: asideWidth };

  return { minimised, style, stageStyle, asideStyle, asideOpen: inset, dragging: offset !== null, onPointerDown };
}
